import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "../../lib/cn";

export interface CopyButtonProps {
  value: string;
  /** Accessible name, e.g. "Copy API key". */
  label: string;
  className?: string;
}

/**
 * Copy-to-clipboard that visibly confirms.
 *
 * Copying used to succeed silently, which is indistinguishable from failing — for a value shown
 * exactly once (an API key, a webhook secret) that ambiguity is expensive, because the user
 * cannot check the clipboard without navigating away and losing the value.
 *
 * The icon swaps to a tick for 1.6s. The timer is cleared on unmount so a copy immediately
 * before a modal closes cannot set state on an unmounted component.
 */
export function CopyButton({ value, label, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard access can be denied (insecure origin, permissions). Fall back to a
      // selection-based copy rather than leaving the button doing nothing at all.
      const el = document.createElement("textarea");
      el.value = value;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(el); }
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1600);
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={label}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-control border border-border bg-surface px-2.5",
        "text-xs font-medium text-text-muted",
        "transition-colors duration-fast ease-standard",
        "hover:bg-surface-hover hover:text-text active:bg-surface-hover",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
        copied && "border-success/30 text-success",
        className,
      )}
    >
      {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
      {copied ? "Copied" : "Copy"}
      {/* Announced to screen readers, which cannot see the icon swap. */}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? "Copied to clipboard" : ""}
      </span>
    </button>
  );
}
